// TeamTakeover: the brand-side view of the same conversation. Unit linked, sources shown,
// a person takes over in one click, and the resolution becomes a draft answer that a person approves.
// Plausible but generic: not a map of the real admin.
import { useMemo } from 'react';
import { cx } from '../ui';
import type { HeroUnit } from '../heroUnits';
import { defaultUnit, qaFor, seriesOf, type Tone } from './data';
import { IconCheck, IconDoc, IconLoop, IconPerson, useAiStyles, useOnScreen, useStream, useTimeline } from './shared';

export type TeamTakeoverProps = { tone?: Tone; unit?: HeroUnit; className?: string };

const STATUS = ['Assistant answering', 'Needs a person', 'You are in', 'You are in', 'Resolved', 'Resolved'];

export function TeamTakeover({ tone = 'light', unit = defaultUnit, className }: TeamTakeoverProps) {
  useAiStyles();
  const qa = qaFor(unit);
  const sr = seriesOf(unit);
  const [ref, onScreen] = useOnScreen<HTMLDivElement>(0.25);
  const durations = useMemo(() => [1700, 1900, 1100, qa.teamReply.split(' ').length * 45 + 500, 2400, 4200], [qa.teamReply]);
  const [step] = useTimeline(durations, onScreen);
  const reply = useStream(qa.teamReply, step === 3, step > 3, 45);
  const others = [
    { s: `${sr}-26-03977`, q: 'Is my warranty still valid?', st: 'Answered' },
    { s: `${sr}-25-01120`, q: 'Can I get the manual in Dutch?', st: 'Answered' },
    { s: `${sr}-26-04410`, q: 'We sold the house. How do we transfer it?', st: 'Answered' },
  ];

  return (
    <div ref={ref} className={cx('ai-team', className)} data-tone={tone}>
      <div
        className="ai-win"
        role="img"
        aria-label={`Your team's inbox: the conversation about ${unit.model} ${unit.serial} is linked to the unit and its sources. A person takes over, replies, and the resolution becomes a draft answer for ${sr} units that a person approves.`}
      >
        <div className="ai-win-bar" aria-hidden="true">
          <span />
          <span />
          <span />
          <b>{unit.brand.toLowerCase()} · conversations</b>
        </div>
        <div className="ai-win-body" aria-hidden="true">
          {/* list */}
          <div className="ai-list">
            <div className="ai-list-h">Today</div>
            <div className={cx('ai-row is-on', step === 1 && 'is-flag')}>
              <div className="ai-row-top">
                <span className="ai-row-serial">{unit.serial}</span>
                <span className={cx('ai-pill', step === 1 && 'ai-pill-warn', step >= 4 && 'ai-pill-ok')}>{step === 1 ? 'Needs a person' : step >= 4 ? 'Resolved' : step >= 2 ? 'You' : 'Assistant'}</span>
              </div>
              <div className="ai-row-q">{qa.short}</div>
            </div>
            {others.map((o) => (
              <div key={o.s} className="ai-row">
                <div className="ai-row-top">
                  <span className="ai-row-serial">{o.s}</span>
                  <span className="ai-pill ai-pill-ok">{o.st}</span>
                </div>
                <div className="ai-row-q">{o.q}</div>
              </div>
            ))}
          </div>

          {/* thread */}
          <div className="ai-thread">
            <div className="ai-thread-h">
              <div>
                <div className="ai-thread-title">{unit.owner}</div>
                <div className="ai-thread-sub">
                  {unit.model} · <span className="font-mono">{unit.serial}</span>
                </div>
              </div>
              <span className={cx('ai-status', step === 1 && 'is-warn', step >= 4 && 'is-ok')}>{STATUS[step]}</span>
            </div>
            <div className="ai-msgs">
              <div className="ai-m ai-m-owner">{qa.q}</div>
              <div className="ai-m ai-m-bot">
                <div className="ai-m-who">Assistant</div>
                {qa.a}
                <div className="ai-m-src">
                  <IconDoc size={11} /> {qa.cite}
                </div>
              </div>
              <div className="ai-m ai-m-owner">{qa.followUp}</div>
              {step >= 2 && <div className="ai-sys ai-cut">You joined. The assistant is paused for this conversation.</div>}
              {step >= 3 && (
                <div className="ai-m ai-m-team">
                  <div className="ai-m-who">You</div>
                  {reply.shown}
                  {!reply.complete && <span className="ai-caret" />}
                </div>
              )}
            </div>
            <div className="ai-thread-foot">
              {step < 2 ? (
                <span className={cx('ai-takeover', step === 1 && 'is-pulse')}>
                  <IconPerson size={13} /> Take over
                </span>
              ) : step < 4 ? (
                <span className="ai-replybox">Reply as {unit.brand} support</span>
              ) : (
                <div className="ai-learn ai-cut">
                  <div className="ai-learn-h">
                    <IconLoop size={13} />
                    {step === 4 ? `Draft answer for ${sr} units` : `Added to answers for ${sr} units`}
                  </div>
                  <div className="ai-learn-t">{qa.learned}</div>
                  <div className="ai-learn-a">
                    {step === 4 ? (
                      <>
                        <span className="ai-approve">Approve</span>
                        <span className="ai-learn-note">Nothing publishes until a person approves</span>
                      </>
                    ) : (
                      <span className="ai-approved">
                        <IconCheck size={13} /> Approved. The next owner gets this first time.
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* unit */}
          <div className="ai-unit">
            <div className="ai-unit-h">This unit</div>
            <dl className="ai-dl">
              <div>
                <dt>Model</dt>
                <dd>{unit.model}</dd>
              </div>
              <div>
                <dt>Serial</dt>
                <dd className="font-mono">{unit.serial}</dd>
              </div>
              <div>
                <dt>{unit.partnerRole[0].toUpperCase() + unit.partnerRole.slice(1)}</dt>
                <dd>{unit.partner}</dd>
              </div>
              <div>
                <dt>Warranty</dt>
                <dd className="ai-ok">{qa.cover}</dd>
              </div>
              <div>
                <dt>History</dt>
                <dd>{qa.history}</dd>
              </div>
            </dl>
            <div className="ai-unit-h mt-4">Sources used</div>
            <ul className="ai-srcs">
              <li>
                <IconDoc size={12} /> {qa.cite}
              </li>
              <li>
                <IconDoc size={12} /> Warranty terms
              </li>
              <li>
                <IconDoc size={12} /> Service record
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamTakeover;
